import type { GetServerSidePropsContext } from 'next'

/**
 * Simple cookie helpers for client-side read/write.
 * These cookies are also readable server-side via `context.req.cookies` in getServerSideProps.
 */

export const COUNTRY_COOKIE = 'country'
export const DEFAULT_COUNTRY = 'SE'

export const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

/**
 * Set a cookie with a given name and value.
 * Uses path=/ and SameSite=Lax so it's sent on all same-site navigations
 * and available in getServerSideProps.
 */
export function setCookie(name: string, value: string, maxAge = COOKIE_MAX_AGE) {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`
}

/**
 * Read a cookie value by name. Returns undefined if not found.
 */
export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Resolve the country code from the request context.
 *
 * Priority: query parameter `?country=XX` → existing cookie → fallback (DEFAULT_COUNTRY).
 *
 * When a query parameter is present it is also persisted as a cookie via
 * Set-Cookie so the value carries over to subsequent navigations.
 */
export function resolveCountryFromRequest(
  context: GetServerSidePropsContext,
  fallback = DEFAULT_COUNTRY,
): string {
  const raw = context.query.country
  const countryFromQuery = typeof raw === 'string' ? raw.toUpperCase() : undefined
  const countryCode = countryFromQuery || context.req.cookies?.[COUNTRY_COOKIE] || fallback

  if (countryFromQuery) {
    context.res.setHeader(
      'Set-Cookie',
      `${COUNTRY_COOKIE}=${encodeURIComponent(countryFromQuery)};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`,
    )
  }

  return countryCode
}
